/**
 * Centralized sys-config cache backed by localStorage.
 *
 * On first load: fetches ALL config types in one call, stores in localStorage.
 * On subsequent loads: reads from localStorage if fresh (< 24 hours).
 * Provides useSysConfig(type) hook for individual type access.
 */

import type { ReactNode } from 'react';
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { apiFetch } from '../api/fetch';
import { API, SYS_CONFIG_TYPES } from '../constants';
import { useAuth } from '../auth';
import type { SysConfigResponse } from '../api/projects';

// ─── All config types to fetch at boot ──────────────────────────────────────

const ALL_CONFIG_TYPES = Object.values(SYS_CONFIG_TYPES);

// ─── localStorage keys ──────────────────────────────────────────────────────

const STORAGE_KEY = 'dvms_sys_configs';
const STORAGE_TS_KEY = 'dvms_sys_configs_ts';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// ─── Types ──────────────────────────────────────────────────────────────────

type SysConfigMap = Record<string, SysConfigResponse[]>;

interface SysConfigCacheValue {
    configs: SysConfigMap;
    loading: boolean;
    /** Force re-fetch from backend (e.g. after admin changes). */
    invalidate: () => void;
}

const SysConfigCacheContext = createContext<SysConfigCacheValue | null>(null);

// ─── Read/write localStorage ────────────────────────────────────────────────

function readFromStorage(): SysConfigMap | null {
    try {
        const ts = localStorage.getItem(STORAGE_TS_KEY);
        if (!ts || Date.now() - Number(ts) > MAX_AGE_MS) {
            return null;
        }
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw) as SysConfigMap;
    } catch {
        return null;
    }
}

function writeToStorage(data: SysConfigMap): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    } catch {
        // storage full or unavailable — ignore
    }
}

/** Clear sys-config localStorage cache (call on logout). */
export function clearSysConfigCache(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_TS_KEY);
    } catch {
        // ignore
    }
}

// ─── Fetch all config types in one call ─────────────────────────────────────

async function fetchAllConfigs(): Promise<SysConfigMap> {
    // Try single call with comma-separated types first, fall back to parallel calls
    try {
        const typesParam = ALL_CONFIG_TYPES.join(',');
        const data = await apiFetch<SysConfigResponse[] | null>(
            `${API.SYS_CONFIGS}?type=${encodeURIComponent(typesParam)}`,
        );
        const items = Array.isArray(data) ? data : [];
        if (items.length > 0) {
            const map: SysConfigMap = {};
            for (const type of ALL_CONFIG_TYPES) {
                map[type] = [];
            }
            for (const item of items) {
                const key = item.configType;
                map[key] ??= [];
                map[key].push(item);
            }
            for (const list of Object.values(map)) {
                list.sort((a, b) => a.sortOrder - b.sortOrder);
            }
            return map;
        }
    } catch {
        // Backend doesn't support comma-separated types — fall back
    }

    // Fallback: fetch each type in parallel
    const results = await Promise.all(
        ALL_CONFIG_TYPES.map(async (type) => {
            const data = await apiFetch<SysConfigResponse[] | null>(
                `${API.SYS_CONFIGS}?type=${encodeURIComponent(type)}`,
            );
            return [type, Array.isArray(data) ? data.sort((a, b) => a.sortOrder - b.sortOrder) : []] as const;
        }),
    );
    return Object.fromEntries(results);
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function SysConfigCacheProvider({ children }: { children: ReactNode }) {
    const { status } = useAuth();
    const [configs, setConfigs] = useState<SysConfigMap>(() => readFromStorage() ?? {});
    const [loading, setLoading] = useState(() => readFromStorage() === null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAllConfigs();
            setConfigs(data);
            writeToStorage(data);
        } catch {
            // failed — keep stale data if any
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch when authenticated and cache is empty or stale
    useEffect(() => {
        if (status !== 'authenticated') {
            // Clear in-memory state on logout
            setConfigs({});
            setLoading(true);
            return;
        }
        const cached = readFromStorage();
        if (cached) {
            setConfigs(cached);
            setLoading(false);
        } else {
            void load();
        }
    }, [status, load]);

    const invalidate = useCallback(() => {
        clearSysConfigCache();
        void load();
    }, [load]);

    return <SysConfigCacheContext value={{ configs, loading, invalidate }}>{children}</SysConfigCacheContext>;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useSysConfigCache(): SysConfigCacheValue {
    const ctx = useContext(SysConfigCacheContext);
    if (!ctx) {
        throw new Error('useSysConfig must be used within SysConfigCacheProvider');
    }
    return ctx;
}

/** Get configs for a specific type from the cache. */
export function useSysConfig(type: string): { data: SysConfigResponse[]; loading: boolean } {
    const { configs, loading } = useSysConfigCache();
    return { data: configs[type] ?? [], loading };
}

/** Invalidate the sys-config cache (call after admin changes). */
export function useSysConfigInvalidate(): () => void {
    const { invalidate } = useSysConfigCache();
    return invalidate;
}
